interface Pipe {
  x: number
  gapY: number
  passed: boolean
}

export interface GameConfig {
  canvas: HTMLCanvasElement
  onScoreChange: (score: number) => void
  onGameOver: (finalScore: number) => void
}

export class Game {
  private ctx: CanvasRenderingContext2D
  private config: GameConfig
  private width: number = 0
  private height: number = 0

  // Bird
  private birdX: number = 0
  private birdY: number = 0
  private birdVelocity: number = 0
  private readonly BIRD_SIZE = 30
  private readonly GRAVITY = 0.15
  private readonly FLAP_FORCE = -4
  private readonly MAX_VELOCITY = 4

  // Pipes
  private pipes: Pipe[] = []
  private readonly PIPE_WIDTH = 60
  private readonly PIPE_GAP = 180
  private readonly PIPE_SPEED = 2.5
  private readonly PIPE_SPACING = 400

  // Game state
  private score: number = 0
  private isGameOver: boolean = false
  private hasStarted: boolean = false
  private animationId: number | null = null

  // Colors
  private readonly COLORS = {
    bg: '#1a1a2e',
    bird: '#ffd93d',
    birdOutline: '#ff6b35',
    pipe: '#4ade80',
    pipeOutline: '#22c55e',
    ground: '#16213e'
  }

  constructor(config: GameConfig) {
    this.config = config
    this.ctx = config.canvas.getContext('2d')!
    this.resize()
    this.reset()
    this.setupInput()
  }

  private resize(): void {
    const rect = this.config.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    // Use visible viewport height to account for browser chrome
    const visibleHeight = window.visualViewport?.height || window.innerHeight
    this.width = rect.width
    this.height = Math.min(rect.height, visibleHeight - 80)

    this.config.canvas.width = this.width * dpr
    this.config.canvas.height = this.height * dpr
    this.ctx.scale(dpr, dpr)

    this.birdX = this.width * 0.2
  }

  private reset(): void {
    this.birdY = this.height / 2
    this.birdVelocity = 0
    this.pipes = []
    this.score = 0
    this.isGameOver = false
    this.hasStarted = false
  }

  private setupInput(): void {
    const flap = () => {
      if (this.isGameOver) return

      if (!this.hasStarted) {
        this.hasStarted = true
      }

      this.birdVelocity = this.FLAP_FORCE
    }

    // Keyboard
    window.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'ArrowUp') {
        flap()
        e.preventDefault()
      }
    })

    // Touch/Click
    this.config.canvas.addEventListener('click', flap)
    this.config.canvas.addEventListener('touchstart', (e) => {
      flap()
      e.preventDefault()
    }, { passive: false })
  }

  private spawnPipe(): void {
    const minGapY = this.PIPE_GAP / 2 + 50
    const maxGapY = this.height - this.PIPE_GAP / 2 - 50
    const gapY = Math.random() * (maxGapY - minGapY) + minGapY

    this.pipes.push({
      x: this.width,
      gapY,
      passed: false
    })
  }

  private update(): void {
    if (this.isGameOver || !this.hasStarted) return

    // Update bird
    this.birdVelocity = Math.min(this.birdVelocity + this.GRAVITY, this.MAX_VELOCITY)
    this.birdY += this.birdVelocity

    // Check ground/ceiling collision
    if (this.birdY < this.BIRD_SIZE / 2 || this.birdY > this.height - this.BIRD_SIZE / 2) {
      this.gameOver()
      return
    }

    // Spawn pipes
    if (this.pipes.length === 0 || this.pipes[this.pipes.length - 1].x < this.width - this.PIPE_SPACING) {
      this.spawnPipe()
    }

    // Update pipes
    for (let i = this.pipes.length - 1; i >= 0; i--) {
      const pipe = this.pipes[i]
      pipe.x -= this.PIPE_SPEED

      // Remove off-screen pipes
      if (pipe.x + this.PIPE_WIDTH < 0) {
        this.pipes.splice(i, 1)
        continue
      }

      // Check collision
      if (this.checkPipeCollision(pipe)) {
        this.gameOver()
        return
      }

      // Score
      if (!pipe.passed && pipe.x + this.PIPE_WIDTH < this.birdX) {
        pipe.passed = true
        this.score++
        this.config.onScoreChange(this.score)
      }
    }
  }

  private checkPipeCollision(pipe: Pipe): boolean {
    const birdLeft = this.birdX - this.BIRD_SIZE / 2
    const birdRight = this.birdX + this.BIRD_SIZE / 2
    const birdTop = this.birdY - this.BIRD_SIZE / 2
    const birdBottom = this.birdY + this.BIRD_SIZE / 2

    const pipeLeft = pipe.x
    const pipeRight = pipe.x + this.PIPE_WIDTH

    // Check if bird is horizontally within pipe
    if (birdRight > pipeLeft && birdLeft < pipeRight) {
      const gapTop = pipe.gapY - this.PIPE_GAP / 2
      const gapBottom = pipe.gapY + this.PIPE_GAP / 2

      // Check if bird is outside the gap
      if (birdTop < gapTop || birdBottom > gapBottom) {
        return true
      }
    }

    return false
  }

  private gameOver(): void {
    this.isGameOver = true
    this.config.onGameOver(this.score)
  }

  private render(): void {
    // Clear
    this.ctx.fillStyle = this.COLORS.bg
    this.ctx.fillRect(0, 0, this.width, this.height)

    // Draw pipes
    for (const pipe of this.pipes) {
      const gapTop = pipe.gapY - this.PIPE_GAP / 2
      const gapBottom = pipe.gapY + this.PIPE_GAP / 2

      // Top pipe
      this.ctx.fillStyle = this.COLORS.pipe
      this.ctx.fillRect(pipe.x, 0, this.PIPE_WIDTH, gapTop)
      this.ctx.strokeStyle = this.COLORS.pipeOutline
      this.ctx.lineWidth = 3
      this.ctx.strokeRect(pipe.x, 0, this.PIPE_WIDTH, gapTop)

      // Top pipe cap
      this.ctx.fillRect(pipe.x - 5, gapTop - 30, this.PIPE_WIDTH + 10, 30)
      this.ctx.strokeRect(pipe.x - 5, gapTop - 30, this.PIPE_WIDTH + 10, 30)

      // Bottom pipe
      this.ctx.fillStyle = this.COLORS.pipe
      this.ctx.fillRect(pipe.x, gapBottom, this.PIPE_WIDTH, this.height - gapBottom)
      this.ctx.strokeRect(pipe.x, gapBottom, this.PIPE_WIDTH, this.height - gapBottom)

      // Bottom pipe cap
      this.ctx.fillRect(pipe.x - 5, gapBottom, this.PIPE_WIDTH + 10, 30)
      this.ctx.strokeRect(pipe.x - 5, gapBottom, this.PIPE_WIDTH + 10, 30)
    }

    // Draw bird
    const birdRotation = Math.min(Math.max(this.birdVelocity * 3, -30), 90) * Math.PI / 180

    this.ctx.save()
    this.ctx.translate(this.birdX, this.birdY)
    this.ctx.rotate(birdRotation)

    // Body
    this.ctx.fillStyle = this.COLORS.bird
    this.ctx.beginPath()
    this.ctx.ellipse(0, 0, this.BIRD_SIZE / 2, this.BIRD_SIZE / 2.5, 0, 0, Math.PI * 2)
    this.ctx.fill()
    this.ctx.strokeStyle = this.COLORS.birdOutline
    this.ctx.lineWidth = 2
    this.ctx.stroke()

    // Eye
    this.ctx.fillStyle = 'white'
    this.ctx.beginPath()
    this.ctx.arc(8, -5, 6, 0, Math.PI * 2)
    this.ctx.fill()
    this.ctx.fillStyle = 'black'
    this.ctx.beginPath()
    this.ctx.arc(10, -5, 3, 0, Math.PI * 2)
    this.ctx.fill()

    // Beak
    this.ctx.fillStyle = '#ff6b35'
    this.ctx.beginPath()
    this.ctx.moveTo(15, 0)
    this.ctx.lineTo(25, 3)
    this.ctx.lineTo(15, 6)
    this.ctx.closePath()
    this.ctx.fill()

    // Wing
    this.ctx.fillStyle = this.COLORS.birdOutline
    this.ctx.beginPath()
    this.ctx.ellipse(-5, 2, 8, 5, -0.3, 0, Math.PI * 2)
    this.ctx.fill()

    this.ctx.restore()

    // Draw "Tap to start" text
    if (!this.hasStarted) {
      this.ctx.fillStyle = 'rgba(255,255,255,0.8)'
      this.ctx.font = 'bold 24px system-ui'
      this.ctx.textAlign = 'center'
      this.ctx.fillText('Tap to Start', this.width / 2, this.height / 2 + 80)
    }
  }

  private gameLoop = (): void => {
    this.update()
    this.render()

    if (!this.isGameOver) {
      this.animationId = requestAnimationFrame(this.gameLoop)
    } else {
      this.animationId = null
    }
  }

  start(): void {
    this.animationId = requestAnimationFrame(this.gameLoop)
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  restart(): void {
    this.reset()
    this.config.onScoreChange(0)
    if (this.animationId === null) {
      this.animationId = requestAnimationFrame(this.gameLoop)
    }
  }

  handleResize(): void {
    this.resize()
  }
}
